import {
  PencilIcon,
  TablePropertiesIcon,
  Trash2Icon,
  XSquareIcon,
  ArrowLeftIcon
} from "lucide-react";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MoonLoader } from "react-spinners";

import { deleteEntity } from "@/api/entity";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  FORM_ENTITIES_DEFAULT_PAGE_SIZE,
  useFormEntities
} from "@/hooks/server-state/use-form-entities";
import { useEntityModalStore } from "@/hooks/store/use-entity-modal-store";

import { extractCategories, formatCategoryNames } from "@/utils/category";

import AlertModal from "@/components/common/modals/alert-modal";
import EntitiesRelationListModal from "@/components/common/modals/entities-relation-list-modal";

import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow
} from "@/components/ui/table";

import type { CategoryType } from "@/types/category";
import type { EntityType } from "@/types/form/entity";

enum ActionTypes {
  SET_FORM = "SET_FORM",
  SET_IS_MODAL_OPEN = "SET_IS_MODAL_OPEN",
  SET_IS_DELETE_MODAL_OPEN = "SET_IS_DELETE_MODAL_OPEN",
  SET_IS_ENTITIES_RELATION_LIST_MODAL_OPEN = "SET_IS_ENTITIES_RELATION_LIST_MODAL_OPEN"
}

type Actions = {
  type: ActionTypes;
  payload?: any;
};

const initialState = {
  isDeleteModalOpen: false,
  isModalOpen: false,
  isEntitiesRelationListModalOpen: false,
  entity: null
};

const entityReducer = (state: typeof initialState, action: Actions) => {
  switch (action.type) {
    case ActionTypes.SET_FORM:
      return { ...state, entity: action.payload };
    case ActionTypes.SET_IS_MODAL_OPEN:
      return { ...state, isModalOpen: action.payload };
    case ActionTypes.SET_IS_DELETE_MODAL_OPEN:
      return { ...state, isDeleteModalOpen: action.payload };
    case ActionTypes.SET_IS_ENTITIES_RELATION_LIST_MODAL_OPEN:
      return { ...state, isEntitiesRelationListModalOpen: action.payload };

    default:
      return state;
  }
};

type CategorySummary = CategoryType & {
  entityCount: number;
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = FORM_ENTITIES_DEFAULT_PAGE_SIZE;

const getPageSize = (value: string | null) => {
  const requestedSize = Number(value);

  return PAGE_SIZE_OPTIONS.includes(requestedSize)
    ? requestedSize
    : DEFAULT_PAGE_SIZE;
};

const getCurrentPage = (value: string | null) => {
  const requestedPage = Number(value);

  return Number.isFinite(requestedPage) && requestedPage > 0
    ? requestedPage
    : 1;
};

const matchesEntitySearch = (entity: EntityType, searchValue: string) => {
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  if (!normalizedSearchValue) {
    return true;
  }

  return [entity.previewName, entity.tableName, entity.description]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedSearchValue));
};

const EntitiesPage = () => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (entityId: number) => deleteEntity(entityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entities"] });
    }
  });

  const { onOpen, setEntity } = useEntityModalStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [entity, dispatch] = useReducer(entityReducer, initialState);
  const [inputValue, setInputValue] = useState(searchParams.get("search") ?? "");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const { data, isLoading, isFetching } = useFormEntities({
    page: 1,
    size: 50,
    search: ""
  });

  useEffect(() => {
    setInputValue(searchParams.get("search") ?? "");
  }, [searchParams]);

  const debouncedSearch = useCallback(
    (value: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setSearchParams((prev) => {
          const params = new URLSearchParams(prev);
          params.set("page", "1");

          if (value.trim()) {
            params.set("search", value.trim());
          } else {
            params.delete("search");
          }

          return params;
        });
      }, 1500);
    },
    [setSearchParams]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  const allEntities = data?.data ?? [];
  const searchValue = searchParams.get("search") ?? "";
  const currentPage = getCurrentPage(searchParams.get("page"));
  const pageSize = getPageSize(searchParams.get("size"));
  const isAllEntitiesView = searchParams.get("view") === "all";

  const selectedCategoryIdValue = Number(searchParams.get("categoryId"));
  const selectedCategoryId =
    !isAllEntitiesView && Number.isFinite(selectedCategoryIdValue) && selectedCategoryIdValue > 0
      ? selectedCategoryIdValue
      : null;

  const categoryRows = useMemo<CategorySummary[]>(() => {
    const categoriesMap = new Map<number, CategorySummary>();

    allEntities.forEach((entityItem: EntityType) => {
      extractCategories(entityItem).forEach((category) => {
        const existingCategory = categoriesMap.get(category.id);

        if (existingCategory) {
          existingCategory.entityCount += 1;
          return;
        }

        categoriesMap.set(category.id, {
          ...category,
          entityCount: 1
        });
      });
    });

    return Array.from(categoriesMap.values()).sort((firstCategory, secondCategory) =>
      firstCategory.name.localeCompare(secondCategory.name, "fa")
    );
  }, [allEntities]);

  const selectedCategory = useMemo(
    () =>
      selectedCategoryId
        ? categoryRows.find((category) => category.id === selectedCategoryId) ?? null
        : null,
    [categoryRows, selectedCategoryId]
  );

  const filteredEntities = useMemo(() => {
    if (isAllEntitiesView) {
      return allEntities.filter((entityItem: EntityType) => matchesEntitySearch(entityItem, searchValue));
    }

    if (!selectedCategory) {
      return [];
    }

    return allEntities.filter((entityItem: EntityType) => {
      const categoryIds = extractCategories(entityItem).map((category) => category.id);

      return categoryIds.includes(selectedCategory.id) && matchesEntitySearch(entityItem, searchValue);
    });
  }, [allEntities, isAllEntitiesView, searchValue, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredEntities.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const visibleEntities = filteredEntities.slice(startIndex, startIndex + pageSize);

  const handleCategorySelect = (categoryId: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("categoryId", categoryId.toString());
      params.delete("view");
      params.set("page", "1");
      params.delete("size");
      return params;
    });
  };

  const handleAllEntitiesSelect = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("view", "all");
      params.delete("categoryId");
      params.set("page", "1");
      params.delete("size");
      return params;
    });
  };

  const handleBackToCategories = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("view");
      params.delete("categoryId");
      params.delete("page");
      params.delete("size");
      return params;
    });
  };

  useEffect(() => {
    if (!selectedCategory && !isAllEntitiesView) {
      return;
    }

    if (isAllEntitiesView) {
      return;
    }

    // Avoid resetting page from URL before data is ready.
    if (isLoading) {
      return;
    }

    if (currentPage > totalPages) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", totalPages.toString());
        return params;
      });
    }
  }, [currentPage, isAllEntitiesView, isLoading, selectedCategory, setSearchParams, totalPages]);

  const showEmptyState = !visibleEntities.length;
  const emptyStateMessage = searchValue.trim()
    ? "نتیجه‌ای مطابق جستجو یافت نشد"
    : "جدولی پیدا نشد";

  if (isLoading) return <Loading />;

  return (
    <>
      <AlertModal
        isOpen={entity.isDeleteModalOpen}
        onClose={() =>
          dispatch({
            type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
            payload: false
          })
        }
        isLoading={isPending}
        onConfirm={async () => {
          mutate(entity?.entity?.id);
          dispatch({
            type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
            payload: false
          });
        }}
        title="آیا از حذف جدول اطمینان دارید؟"
        description="این عملیات قابل برگشت نخواهد بود و فرم بصورت دائمی حذف خواهد شد"
      />

      <EntitiesRelationListModal
        isOpen={entity.isEntitiesRelationListModalOpen}
        entityId={entity.entity?.id}
        onClose={() => {
          dispatch({
            type: ActionTypes.SET_IS_ENTITIES_RELATION_LIST_MODAL_OPEN,
            payload: false
          });
        }}
      />

      {selectedCategory || isAllEntitiesView ? (
        <>
          <div className="flex items-start justify-between gap-3 px-5 py-2">
            <div className="flex flex-col gap-2">
              <button
                className="flex items-center gap-x-1 text-sm hover:text-primary"
                onClick={handleBackToCategories}
              >
                <ArrowLeftIcon size={14} className="text-primary" />
                بازگشت به دسته بندی ها
              </button>

              <div className="text-sm text-slate-600">
                {isAllEntitiesView ? (
                  <>همه جدول ها</>
                ) : (
                  <>
                    جدول‌های دسته بندی <span className="font-semibold text-slate-900">{selectedCategory?.name}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex w-full items-center gap-2 sm:w-60">
                <Input
                  value={inputValue}
                  onChange={handleSearchChange}
                  placeholder="جستجو بر اساس نام یا توضیحات"
                />
                {isFetching && !isLoading && (
                  <MoonLoader color="#0099A5" size={20} speedMultiplier={0.75} />
                )}
              </div>

              <button
                className="flex items-center gap-x-2 text-sm font-semibold hover:text-primary"
                onClick={() => {
                  onOpen();
                  setEntity(null);
                }}
              >
                <XSquareIcon size={16} className="text-primary" />
                ساخت جدول جدید
              </button>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام نمایشی</TableHead>
                <TableHead>نام جدول</TableHead>
                <TableHead>توضیحات</TableHead>
                <TableHead>دسته بندی ها</TableHead>
                <TableHead>ارتباطات</TableHead>
                <TableHead>ویرایش</TableHead>
                <TableHead>حذف</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleEntities.map((formEntity, index) => (
                <TableRow
                  key={formEntity.id}
                  onClick={() => {
                    navigate(formEntity.id.toString());
                  }}
                  className="cursor-pointer"
                >
                  <TableCell>{startIndex + index + 1}</TableCell>
                  <TableCell>{formEntity?.previewName}</TableCell>
                  <TableCell>{formEntity?.tableName}</TableCell>
                  <TableCell>{formEntity?.description}</TableCell>
                  <TableCell className="max-w-xs whitespace-normal">
                    <p>{formEntity?.categories?.join(", ")}</p>
                    {formatCategoryNames(formEntity) || "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch({
                          type: ActionTypes.SET_IS_ENTITIES_RELATION_LIST_MODAL_OPEN,
                          payload: true
                        });
                        dispatch({
                          type: ActionTypes.SET_FORM,
                          payload: formEntity
                        });
                      }}
                    >
                      <TablePropertiesIcon className="text-slate-700" />
                    </button>
                  </TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEntity(formEntity);
                        onOpen();
                      }}
                    >
                      <PencilIcon className="text-slate-700" />
                    </button>
                  </TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch({
                          type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
                          payload: true
                        });
                        dispatch({
                          type: ActionTypes.SET_FORM,
                          payload: formEntity
                        });
                      }}
                    >
                      <Trash2Icon className="text-red-500" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {showEmptyState && <EmptyState message={emptyStateMessage} />}
          <TablePagination totalItems={filteredEntities.length} />
        </>
      ) : (
        <>
          <div className="flex items-center justify-between px-5 py-2">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-slate-900">دسته بندی جدول ها</p>
              <p className="mt-1 text-xs text-slate-500">یک دسته بندی را انتخاب کنید تا جدول های مربوط به آن نمایش داده شود.</p>
            </div>

            <button
              className="flex items-center gap-x-2 text-sm font-semibold hover:text-primary"
              onClick={() => {
                onOpen();
                setEntity(null);
              }}
            >
              <XSquareIcon size={16} className="text-primary" />
              ساخت جدول جدید
            </button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام دسته بندی</TableHead>
                <TableHead>تعداد جدول</TableHead>
                <TableHead>توضیحات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="cursor-pointer" onClick={handleAllEntitiesSelect}>
                <TableCell>1</TableCell>
                <TableCell className="font-medium text-primary">همه</TableCell>
                <TableCell>{allEntities.length}</TableCell>
                <TableCell>نمایش همه جدول ها</TableCell>
              </TableRow>

              {categoryRows.map((categoryItem, index) => (
                <TableRow
                  key={categoryItem.id}
                  className="cursor-pointer"
                  onClick={() => handleCategorySelect(categoryItem.id)}
                >
                  <TableCell>{index + 2}</TableCell>
                  <TableCell>{categoryItem.name}</TableCell>
                  <TableCell>{categoryItem.entityCount}</TableCell>
                  <TableCell>{categoryItem.description || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!categoryRows.length && <EmptyState message="دسته بندی پیدا نشد" />}
        </>
      )}
    </>
  );
};

const Loading = () => (
  <div className="flex h-[calc(100vh/2)] w-full items-center justify-center">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

const EmptyState = ({ message = "جدولی پیدا نشد" }: { message?: string }) => (
  <div className="flex h-32 w-full items-center justify-center bg-white shadow-md">
    <p className="text-slate-500">{message}</p>
  </div>
);

export default EntitiesPage;
